import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileText, FileType2, FileSpreadsheet, FileImage, File as FileIcon, Download, Pencil, RefreshCw, Trash2, Loader2, History, Check, X } from "lucide-react";
import { api, errMsg, fmtDate, fmtDay, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ReasonDialog } from "@/components/forms/ReasonDialog";

const sel = "h-10 rounded-md border border-slate-300 bg-white text-sm px-2";
const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.heic,.txt";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const icon = (ext) => ({ pdf: [FileType2, "text-rose-600"], doc: [FileText, "text-sky-600"], docx: [FileText, "text-sky-600"], xls: [FileSpreadsheet, "text-emerald-600"],
  xlsx: [FileSpreadsheet, "text-emerald-600"], csv: [FileSpreadsheet, "text-emerald-600"] }[ext] || (["png", "jpg", "jpeg", "webp", "heic"].includes(ext) ? [FileImage, "text-violet-600"] : [FileIcon, "text-slate-500"]));
const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const Uploader = ({ cid, onDone }) => {
  const ref = useRef();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const send = async (files) => {
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    fd.append("report_date", date);
    try { const r = await api.post(`/committees/${cid}/office-reports`, fd, { timeout: 300000 }); toast.success(`${r.data.length} report(s) uploaded`); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border-2 border-dashed border-amber-300/70 bg-amber-50/40 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); send(e.dataTransfer.files); }} data-testid="report-dropzone">
      <UploadCloud className="w-7 h-7 text-amber-600 shrink-0" />
      <div className="flex-1 text-sm"><div className="font-semibold text-slate-900">Drop report files here</div><div className="text-xs text-slate-500">Word, PDF, Excel or photo/scan · up to 20 MB each · several at once</div></div>
      <label className="text-xs text-slate-600 flex flex-col">Report date
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-white h-10 mt-1" data-testid="report-upload-date" /></label>
      <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { send(e.target.files); e.target.value = ""; }} data-testid="report-upload-input" />
      <Button className="h-11" disabled={busy} onClick={() => ref.current?.click()} data-testid="report-upload-button">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-1" />}Upload reports</Button>
    </div>
  );
};

const ReportRow = ({ r, cid, canManage, isSA, onChange, onDelete }) => {
  const [edit, setEdit] = useState(null);
  const [showV, setShowV] = useState(false);
  const rep = useRef();
  const [Icon, color] = icon(r.current.ext);
  const base = `/committees/${cid}/office-reports/${r.report_id}`;
  const dl = (v) => downloadBlob(`${base}/download${v ? `?version=${v.version}` : ""}`, (v || r.current).filename).catch((e) => toast.error(errMsg(e)));
  const save = async () => { try { await api.patch(base, edit); toast.success("Report updated"); setEdit(null); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  const replace = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`${base}/replace`, fd, { timeout: 300000 }); toast.success("New version uploaded"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="p-4 fade-up" data-testid={`report-row-${r.report_id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Icon className={`w-8 h-8 shrink-0 ${color}`} />
        {edit ? (
          <div className="flex-1 flex flex-col sm:flex-row gap-2">
            <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="bg-white" data-testid="report-edit-title" />
            <Input type="date" value={edit.report_date} onChange={(e) => setEdit({ ...edit, report_date: e.target.value })} className="bg-white sm:w-44" data-testid="report-edit-date" />
            <Button size="icon" variant="ghost" onClick={save} data-testid="report-edit-save"><Check className="w-4 h-4 text-emerald-600" /></Button>
            <Button size="icon" variant="ghost" onClick={() => setEdit(null)} data-testid="report-edit-cancel"><X className="w-4 h-4" /></Button>
          </div>
        ) : (
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 truncate" data-testid={`report-title-${r.report_id}`}>{r.title}</div>
            <div className="text-xs text-slate-500">{fmtDay(r.report_date)} · {size(r.current.size)} · {r.current.ext.toUpperCase()} · uploaded by {r.current.uploaded_by_name} {fmtDate(r.current.uploaded_at)}
              {r.versions.length > 1 && <button className="ml-2 text-amber-700 underline" onClick={() => setShowV(!showV)} data-testid={`report-versions-toggle-${r.report_id}`}>v{r.versions.length} · {r.versions.length - 1} earlier</button>}</div>
          </div>
        )}
        {!edit && <div className="flex gap-1 flex-wrap">
          <Button variant="outline" className="h-10" onClick={() => dl()} data-testid={`report-download-${r.report_id}`}><Download className="w-4 h-4 mr-1" />Download</Button>
          {canManage && <>
            <Button size="icon" variant="ghost" className="h-10 w-10" onClick={() => setEdit({ title: r.title, report_date: r.report_date })} data-testid={`report-edit-${r.report_id}`}><Pencil className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" className="h-10 w-10" title="Replace with newer version" onClick={() => rep.current?.click()} data-testid={`report-replace-${r.report_id}`}><RefreshCw className="w-4 h-4" /></Button>
            <input ref={rep} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { e.target.files[0] && replace(e.target.files[0]); e.target.value = ""; }} data-testid={`report-replace-input-${r.report_id}`} />
          </>}
          {isSA && <Button size="icon" variant="ghost" className="h-10 w-10" onClick={() => onDelete(r)} data-testid={`report-delete-${r.report_id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
        </div>}
      </div>
      {showV && (
        <div className="mt-3 ml-0 sm:ml-11 rounded-md border border-slate-200 divide-y divide-slate-100 text-xs" data-testid={`report-versions-${r.report_id}`}>
          {[...r.versions].reverse().map((v) => (
            <div key={v.version} className="flex items-center gap-2 px-3 py-2">
              <History className="w-3.5 h-3.5 text-slate-400" /><span className="font-mono">v{v.version}</span>
              <span className="truncate flex-1">{v.filename} · {size(v.size)} · {v.uploaded_by_name} {fmtDate(v.uploaded_at)}</span>
              <Button size="sm" variant="ghost" onClick={() => dl(v)} data-testid={`report-version-download-${r.report_id}-${v.version}`}><Download className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ReportsTab = ({ cid, info, onChange }) => {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ q: "", year: "", month: "" });
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get(`/committees/${cid}/office-reports`, { params: f }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [cid, f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const changed = () => { load(); onChange(); };
  return (
    <div className="space-y-4">
      {info.can_manage && <Uploader cid={cid} onDone={changed} />}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col sm:flex-row gap-2 p-3 border-b border-slate-100">
          <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search report titles…" className="flex-1 bg-white h-10" data-testid="report-search-input" />
          <select className={sel} value={f.year} onChange={(e) => setF({ ...f, year: e.target.value, month: "" })} data-testid="report-filter-year">
            <option value="">All years</option>{(d?.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className={sel} value={f.month} disabled={!f.year} onChange={(e) => setF({ ...f, month: e.target.value })} data-testid="report-filter-month">
            <option value="">All months</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        {!d ? <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
          <div className="divide-y divide-slate-100" data-testid="report-list">
            {d.items.length === 0 && <p className="p-10 text-center text-sm text-slate-500" data-testid="report-empty">No reports yet.</p>}
            {d.items.map((r) => <ReportRow key={r.report_id} r={r} cid={cid} canManage={info.can_manage} isSA={info.is_super_admin} onChange={changed} onDelete={setDel} />)}
          </div>
        )}
      </div>
      <ReasonDialog open={!!del} onClose={() => setDel(null)} danger title={`Permanently delete "${del?.title}"?`} description="All versions are deleted. The reason is recorded in the audit log." confirmLabel="Delete report"
        onConfirm={async (reason) => { try { await api.delete(`/committees/${cid}/office-reports/${del.report_id}`, { data: { reason } }); toast.success("Report deleted"); changed(); } catch (e) { toast.error(errMsg(e)); } }} />
    </div>
  );
};
